import React from "react";
import { Drawer, useMediaQuery, useTheme, IconButton, Box, Typography, FormControlLabel, Switch } from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import BDrawer from "../BDrawer";
import HistoryDrawerContent from "./HistoryDrawerContent";

export interface HistoryItem {
  id: string;
  name: string; // Maps to 'title' from API
  timestamp: string | Date;
  workflow?: string; // Additional fields from API
  workflowType?: string;
  companyId?: string;
  isMyDocument?: boolean;
  userName?: string;
  jobId?: string;
}

export interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  items?: HistoryItem[];
  selectedItemId?: string;
  onItemClick?: (item: HistoryItem) => void;
  onEdit?: (item: HistoryItem) => void;
  onDelete?: (item: HistoryItem) => void;
  isLoading?: boolean;
  showCompanyHistory?: boolean;
  onCompanyHistoryToggle?: (enabled: boolean) => void;
  showNameAsTitle?: boolean;
}

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  open,
  onClose,
  items,
  selectedItemId,
  onItemClick,
  onEdit,
  onDelete,
  isLoading = false,
  showCompanyHistory = false,
  onCompanyHistoryToggle,
  showNameAsTitle = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  if (isMobile) {
    // Bottom drawer for screens smaller than lg
    return (
      <BDrawer
        open={open}
        onClose={onClose}
        anchor="bottom"
        title={t("components.historyDrawer.title")}
        showTopBar={true}
        showBottomBar={false}
      >
        <HistoryDrawerContent
          items={items}
          selectedItemId={selectedItemId}
          onItemClick={onItemClick}
          onEdit={onEdit}
          onDelete={onDelete}
          isLoading={isLoading}
          showTitle={true}
          showCompanyHistory={showCompanyHistory}
          onCompanyHistoryToggle={onCompanyHistoryToggle}
          showNameAsTitle={showNameAsTitle}
        />
      </BDrawer>
    );
  }

  // Right drawer for desktop
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {t("components.historyDrawer.title")}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              backgroundColor: theme.palette.action.hover,
              border:"none"
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>
        
        {onCompanyHistoryToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={showCompanyHistory}
                onChange={(e) => onCompanyHistoryToggle(e.target.checked)}
                size="medium"
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                {t("components.historyDrawer.showCompanyHistoryToggle")}
              </Typography>
            }
            sx={{ m: 0 }}
          />
        )}
      </Box>
      <HistoryDrawerContent
        items={items}
        selectedItemId={selectedItemId}
        onItemClick={onItemClick}
        onEdit={onEdit}
        onDelete={onDelete}
        showTitle={false}
        isLoading={isLoading}
        showCompanyHistory={showCompanyHistory}
        onCompanyHistoryToggle={onCompanyHistoryToggle}
        showNameAsTitle={showNameAsTitle}
      />
    </Drawer>
  );
};

export default HistoryDrawer;

